import { useEffect, useCallback } from 'react';
import { useUIV2Safe } from '@/contexts/UIV2Context';
import { useKonamiCode } from './useKonamiCode';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

/**
 * Hook to manage surprise mode features
 * - Konami code triggers party mode
 * - Auto-disables in LOW_FX mode or reduced motion
 */
export function useSurpriseMode() {
  const { surpriseMode, triggerPartyMode, isLowFx, partyMode } = useUIV2Safe();
  
  const handleKonamiActivated = useCallback(() => {
    if (isLowFx || !surpriseMode) {
      return;
    }
    
    // Trigger party mode
    triggerPartyMode();
    
    // Show toast
    toast('🎉 Party Mode Activated!', {
      description: 'Enjoy the light show for 15 seconds!',
      duration: 3000,
    });
    
    // Fire confetti
    const duration = 5000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    const randomInRange = (min: number, max: number) => {
      return Math.random() * (max - min) + min;
    };

    const interval: ReturnType<typeof setInterval> = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }

      const particleCount = 50 * (timeLeft / duration);

      // Fire from both sides
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      });
    }, 250);
  }, [isLowFx, surpriseMode, triggerPartyMode]);
  
  // Listen for Konami code
  useKonamiCode({
    onActivate: handleKonamiActivated,
    enabled: surpriseMode && !isLowFx,
  });
  
  return {
    isActive: partyMode,
    surpriseModeEnabled: surpriseMode,
  };
}

/**
 * Hook to trigger achievement sparks
 * Client-side milestone celebrations
 */
export function useAchievementSpark() {
  const { isLowFx, surpriseMode } = useUIV2Safe();
  
  const triggerSpark = useCallback((message?: string) => {
    if (isLowFx || !surpriseMode) return;
    
    // Quick burst of confetti
    confetti({
      particleCount: 30,
      spread: 60,
      origin: { y: 0.7 },
      colors: ['#38bdf8', '#c084fc', '#fbbf24'],
      ticks: 100,
      zIndex: 9999,
    });
    
    if (message) {
      toast('✨ Achievement!', {
        description: message,
        duration: 2000,
      });
    }
  }, [isLowFx, surpriseMode]);
  
  return { triggerSpark };
}
